import { ScanEye } from 'lucide-react';
import React, { useEffect } from 'react';
import { toast } from 'sonner';

import { Switch } from '@/components/ui/switch';
import { applyReactScanEnabled, isReactScanAvailable } from '@/reactScanInit';
import { useStore } from '@/store/store';

interface ReactScanToggleProps {
  className?: string;
}

/**
 * Toggles the react-scan performance overlay on/off at runtime.
 *
 * react-scan is bootstrapped from the npm package in src/reactScanInit.ts. This
 * control syncs its runtime to the persisted store value on mount and on every
 * subsequent toggle via `applyReactScanEnabled`.
 *
 * Active in local dev and on Vercel preview deployments. Renders nothing in real
 * production, where react-scan is excluded from the bundle and
 * `isReactScanAvailable` is false.
 */
export const ReactScanToggle: React.FC<ReactScanToggleProps> = ({ className = '' }) => {
  const { reactScanEnabled, setReactScanEnabled } = useStore();

  // Keep the react-scan runtime in sync with the persisted toggle state.
  useEffect(() => {
    applyReactScanEnabled(reactScanEnabled);
  }, [reactScanEnabled]);

  // react-scan is dev-only; hide the control entirely in production builds.
  if (!isReactScanAvailable) {
    return null;
  }

  const handleToggle = () => {
    const newState = !reactScanEnabled;
    setReactScanEnabled(newState);
    toast.success(newState ? 'React Scan activated' : 'React Scan deactivated', {
      duration: 3000,
    });
  };

  return (
    <div className={`flex items-center justify-between gap-3 whitespace-nowrap ${className}`}>
      <div className="flex items-center gap-2">
        <ScanEye className="h-4 w-4 shrink-0" />
        <label htmlFor="react-scan-toggle" className="text-sm font-medium cursor-pointer">
          React Scan
        </label>
      </div>
      <Switch
        id="react-scan-toggle"
        checked={reactScanEnabled}
        onCheckedChange={handleToggle}
        aria-label={reactScanEnabled ? 'Deactivate React Scan' : 'Activate React Scan'}
        className="data-[state=checked]:bg-orange-600"
      />
    </div>
  );
};
